import { useId, useState } from 'react'
import { Ban, BookText, Building2, CircleAlert, CircleCheck, Landmark, ListTodo, MonitorSmartphone, PencilLine, Receipt, Repeat, Tags, Wallet } from 'lucide-react'
import { NAAM_VAN, type ExactVelden, type Functie, type RelatieVelden, type RelatieWijzigVelden, type TaakVelden, type TerugkerendVelden, type TicketVelden, type Voorstel } from '../../../gedeeld/types'
import { c } from '../../lib/api'
import { Markdown } from '../../lib/markdown'
import { controleerTerugkerend, invoerDatumLang, PRIORITEITEN, schemaZin, vandaagInvoer, veldenVan } from '../../lib/opmaak'
import { meld, meldFout, useMensen, useStaat } from '../../lib/staat'
import TerugkerendFormulier, { type Fouten } from '../taken/TerugkerendFormulier'
import { Knop } from '../ui'
import ExactFormulier, { ExactSamenvatting } from './ExactFormulier'
import RelatieFormulier, { EXACT_URL } from './RelatieFormulier'
import RelatieWijzigFormulier from './RelatieWijzigFormulier'

/* ===========================================================================
 *  Een voorstel van Cowork, als kaart
 *
 *  Casper: "alles wat de AI invult moet te corrigeren zijn". Een leesveld
 *  maakt een misgelezen waarde onherstelbaar: als de AI "Wim" las waar
 *  "Willem" stond, moet je dat hier kunnen veranderen vóór er een taak op
 *  de verkeerde naam staat. Dus zijn ALLE velden open, en gaat er met
 *  Uitvoeren mee wat er nu in staat -- niet wat de AI oorspronkelijk
 *  voorstelde.
 *
 *  Na de klik toont de kaart wat er gebeurde, in de woorden van de server.
 * =========================================================================== */

const isTaak = (v: Voorstel): v is Voorstel & { soort: 'taak'; velden: TaakVelden } => v.soort === 'taak'
const isTicket = (v: Voorstel): v is Voorstel & { soort: 'ticket'; velden: TicketVelden } => v.soort === 'ticket'
const isRelatie = (v: Voorstel): v is Voorstel & { soort: 'relatie'; velden: RelatieVelden } => v.soort === 'relatie'
const isWijzig = (v: Voorstel): v is Voorstel & { soort: 'relatie-wijzigen'; velden: RelatieWijzigVelden } => v.soort === 'relatie-wijzigen'
const isExact = (v: Voorstel): v is Voorstel & { soort: 'exact'; velden: ExactVelden } => v.soort === 'exact'

/* Schrijven in Exact (0219): een eigen icoon per soort, zodat je een
   bankboeking niet voor een grootboekrekening aanziet. */
const EXACT_ICOON: Record<Functie, typeof BookText> = {
  grootboek: BookText, kostenplaats: Tags, inkoop: Receipt, verkoop: Receipt,
  memoriaal: BookText, bank: Landmark, kas: Wallet, verkoopfactuur: Receipt,
}

/* Gelijk aan de categorieen in IT (src/lib/woorden.ts van truckwash-it). */
const IT_CATEGORIEEN: { key: TicketVelden['categorie']; label: string }[] = [
  { key: 'hardware', label: 'Laptop, pc of scherm' },
  { key: 'telefoon', label: 'Telefoon of tablet' },
  { key: 'printer', label: 'Printer' },
  { key: 'netwerk', label: 'Internet of wifi' },
  { key: 'account', label: 'Inloggen of wachtwoord' },
  { key: 'mail', label: 'Mail' },
  { key: 'software', label: 'Programma of app' },
  { key: 'toegang', label: 'Toegang tot een map of systeem' },
  { key: 'beveiliging', label: 'Beveiliging (verdachte mail, kwijt apparaat)' },
  { key: 'overig', label: 'Iets anders' },
]
const IT_PRIORITEIT: { key: TicketVelden['prioriteit']; label: string; kleur: string }[] = [
  { key: 'laag', label: 'Kan wachten', kleur: 'ok' },
  { key: 'normaal', label: 'Normaal', kleur: 'info' },
  { key: 'hoog', label: 'Ik kan slecht verder', kleur: 'oranje' },
]

export default function VoorstelKaart({ berichtId, voorstel, onVoorstel }: {
  berichtId: string
  voorstel: Voorstel
  onVoorstel: (v: Voorstel) => void
}) {
  const [bezig, setBezig] = useState<'uitvoeren' | 'afwijzen' | null>(null)
  const [taak, setTaak] = useState<TaakVelden | null>(() => (isTaak(voorstel) ? { ...voorstel.velden } : null))
  const [ticket, setTicket] = useState<TicketVelden | null>(() => (isTicket(voorstel) ? { ...voorstel.velden } : null))
  const [relatie, setRelatie] = useState<RelatieVelden | null>(() => (isRelatie(voorstel) ? structuredClone(voorstel.velden) : null))
  const [wijzig, setWijzig] = useState<RelatieWijzigVelden | null>(() => (isWijzig(voorstel) ? structuredClone(voorstel.velden) : null))
  const [schema, setSchema] = useState<TerugkerendVelden | null>(() => (
    isTaak(voorstel) || isTicket(voorstel) || isRelatie(voorstel) || isWijzig(voorstel) || isExact(voorstel) ? null : veldenVan(voorstel.velden as TerugkerendVelden)
  ))
  const [fouten, setFouten] = useState<Fouten & { titel?: string }>({})
  const open = voorstel.status === 'open'

  async function besluit(b: 'uitvoeren' | 'afwijzen') {
    if (b === 'uitvoeren') {
      if (taak) {
        if (!taak.titel.trim()) { setFouten({ titel: 'Een taak heeft een titel nodig.' }); return }
      } else if (ticket) {
        if (!ticket.titel.trim()) { setFouten({ titel: 'Zet er kort bij wat er aan de hand is.' }); return }
      } else if (relatie) {
        if (!relatie.relatie.naam.trim()) { meldFout(new Error('Een relatie heeft een naam nodig.')); return }
        if (!relatie.divisies.length) { meldFout(new Error("Vink aan in welke bv's hij moet.")); return }
      } else if (wijzig) {
        if (!wijzig.verwijderen && wijzig.nieuweIban && !wijzig.ibanBevestigd) { meldFout(new Error('Bevestig eerst dat je het nieuwe rekeningnummer hebt nagebeld.')); return }
      } else if (schema) {
        const f = controleerTerugkerend(schema)
        setFouten(f)
        if (Object.keys(f).length) return
      }
    }
    setBezig(b)
    try {
      const velden = taak ? { ...taak, titel: taak.titel.trim() } : ticket ? { ...ticket, titel: ticket.titel.trim() } : relatie ?? wijzig ?? schema!
      const { voorstel: nieuw } = await c('voorstel', {
        berichtId, voorstelId: voorstel.id, besluit: b,
        ...(b === 'uitvoeren' ? { velden } : {}),
      })
      onVoorstel(nieuw)
      if (nieuw.status === 'uitgevoerd') meld(taak ? 'De taak staat erin.' : ticket ? 'Gemeld bij IT.' : relatie ? 'Aangemaakt in Exact.' : wijzig ? (wijzig.verwijderen ? 'Verwijderd uit Exact.' : 'Gewijzigd in Exact.') : 'De terugkerende taak staat aan.')
      if (nieuw.status === 'mislukt') meld(nieuw.uitkomst ?? 'Uitvoeren lukte niet.', 'fout')
    } catch (e) {
      meldFout(e)
    } finally {
      setBezig(null)
    }
  }

  const Icoon = isExact(voorstel) ? EXACT_ICOON[voorstel.velden.functie] ?? BookText : isTaak(voorstel) ? ListTodo : isTicket(voorstel) ? MonitorSmartphone : isRelatie(voorstel) ? Building2 : isWijzig(voorstel) ? PencilLine : Repeat

  return (
    <section className={`voorstel ${voorstel.status}`} aria-label={`Voorstel: ${voorstel.titel}`}>
      <header className="voorstel-kop">
        <span className="voorstel-icoon" aria-hidden><Icoon size={15} /></span>
        <div className="vul">
          <span className="voorstel-soort">{isExact(voorstel) ? `Voorstel: ${(NAAM_VAN[voorstel.velden.functie] ?? 'iets').toLowerCase()} in Exact` : isTaak(voorstel) ? 'Voorstel: een taak' : isTicket(voorstel) ? 'Voorstel: een melding bij IT' : isRelatie(voorstel) ? 'Voorstel: een relatie in Exact' : isWijzig(voorstel) ? 'Voorstel: een relatie wijzigen in Exact' : 'Voorstel: een terugkerende taak'}</span>
          <h3>{voorstel.titel}</h3>
        </div>
        {!open && <Uitkomst v={voorstel} />}
      </header>

      {open && isExact(voorstel) ? (
        <>
          <p className="voorstel-uitleg">Cowork vulde dit in. Klopt er iets niet, pas het dan aan; de kaart wordt na elke wijziging opnieuw nagekeken.</p>
          <ExactFormulier berichtId={berichtId} voorstel={voorstel} onVoorstel={onVoorstel} />
        </>
      ) : open ? (
        <>
          <p className="voorstel-uitleg">Cowork vulde dit in. Klopt er iets niet, pas het dan aan voordat je het uitvoert.</p>
          {taak && <TaakFormulier v={taak} onV={setTaak} fout={fouten.titel} />}
          {ticket && <TicketFormulier v={ticket} onV={setTicket} fout={fouten.titel} />}
          {relatie && <RelatieFormulier v={relatie} onV={setRelatie} />}
          {wijzig && <RelatieWijzigFormulier v={wijzig} onV={setWijzig} />}
          {schema && <TerugkerendFormulier v={schema} onV={(x) => { setSchema(x); if (Object.keys(fouten).length) setFouten(controleerTerugkerend(x)) }} fouten={fouten} compact />}
          <footer className="voorstel-voet">
            <Knop soort="stil" onClick={() => void besluit('afwijzen')} bezig={bezig === 'afwijzen'} disabled={!!bezig}>Afwijzen</Knop>
            <Knop soort="hoofd" onClick={() => void besluit('uitvoeren')} bezig={bezig === 'uitvoeren'} disabled={!!bezig}>Uitvoeren</Knop>
          </footer>
        </>
      ) : (
        <div className="voorstel-na">
          {/* Als markdown: dan wordt de link naar het IT-ticket ("Volgen kan op
              https://it...") klikbaar. Alleen http(s) en mailto, zie markdown.tsx. */}
          {voorstel.uitkomst && <Markdown tekst={voorstel.uitkomst} klein />}
          <Samenvatting v={voorstel} />
        </div>
      )}
    </section>
  )
}

function Uitkomst({ v }: { v: Voorstel }) {
  if (v.status === 'uitgevoerd') return <span className="badge t-ok"><CircleCheck size={12} /> Uitgevoerd</span>
  if (v.status === 'afgewezen') return <span className="badge"><Ban size={12} /> Afgewezen</span>
  if (v.status === 'bezig') return <span className="badge t-info">Wordt uitgevoerd…</span>
  return <span className="badge t-danger"><CircleAlert size={12} /> Mislukt</span>
}

/** Na afloop: kort wat er (niet) is gedaan, zodat je het later nog terugleest. */
function Samenvatting({ v }: { v: Voorstel }) {
  if (isExact(v)) return <ExactSamenvatting v={v.velden} />
  if (isWijzig(v)) {
    const w = v.velden
    return (
      <dl className="voorstel-dl">
        <dt>Relatie</dt><dd><a href={`${EXACT_URL}/relatie/${w.division}/${encodeURIComponent(w.code)}`} target="_blank" rel="noreferrer">{w.relatie.naam} ({w.bvNaam}, {w.code})</a></dd>
        {w.wat.length > 0 && <><dt>{w.verwijderen ? 'Verwijderd' : 'Veranderd'}</dt><dd>{w.wat.join('; ')}</dd></>}
      </dl>
    )
  }
  if (isRelatie(v)) {
    const r = v.velden
    const gemaakt = (r.uitkomsten ?? []).filter((u) => u.hoe === 'gemaakt' && u.relatie)
    return (
      <dl className="voorstel-dl">
        <dt>Relatie</dt><dd>{r.relatie.naam}</dd>
        {gemaakt.length > 0 && <><dt>In Exact</dt><dd>{gemaakt.map((u, i) => (
          <span key={u.division}>{i > 0 && ', '}
            <a href={`${EXACT_URL}/relatie/${u.division}/${encodeURIComponent(u.relatie!.code)}`} target="_blank" rel="noreferrer">{u.naam} ({u.relatie!.code})</a>
          </span>
        ))}</dd></>}
        {r.relatie.iban && <><dt>IBAN</dt><dd className="mono">{r.relatie.iban}</dd></>}
      </dl>
    )
  }
  if (isTicket(v)) {
    const t = v.velden
    return (
      <dl className="voorstel-dl">
        <dt>Melding</dt><dd>{t.titel}</dd>
        <dt>Waarover</dt><dd>{IT_CATEGORIEEN.find((c) => c.key === t.categorie)?.label ?? t.categorie}</dd>
      </dl>
    )
  }
  if (isTaak(v)) {
    const t = v.velden
    const p = PRIORITEITEN.find((x) => x.key === t.prioriteit)
    return (
      <dl className="voorstel-dl">
        <dt>Taak</dt><dd>{t.titel}</dd>
        <dt>Voor</dt><dd>{t.toegewezenNaam ?? 'Jezelf'}</dd>
        {t.deadline && <><dt>Deadline</dt><dd>{invoerDatumLang(t.deadline)}</dd></>}
        <dt>Prioriteit</dt><dd>{p?.label ?? t.prioriteit}</dd>
      </dl>
    )
  }
  const s = v.velden as TerugkerendVelden
  return (
    <dl className="voorstel-dl">
      <dt>Naam</dt><dd>{s.naam}</dd>
      <dt>Schema</dt><dd>{schemaZin(s)}</dd>
    </dl>
  )
}

/* ------------------------------------------------------------------ *
 *  De velden van een gewone taak
 * ------------------------------------------------------------------ */

function TaakFormulier({ v, onV, fout }: { v: TaakVelden; onV: (v: TaakVelden) => void; fout?: string }) {
  const id = useId()
  const ik = useStaat((s) => s.ik)
  const mensen = useMensen()
  const zet = <K extends keyof TaakVelden>(k: K, w: TaakVelden[K]) => onV({ ...v, [k]: w })

  /* Wie de AI noemde maar niet in de lijst staat (nog niet geladen, of geen
     collega die jij mag zien), blijft kiesbaar: anders verdwijnt hij stil
     uit het veld. */
  const anderen = mensen.filter((m) => m.id !== ik?.id)
  const onbekend = v.toegewezenAan && !mensen.some((m) => m.id === v.toegewezenAan)
    ? { id: v.toegewezenAan, naam: v.toegewezenNaam ?? 'Onbekend', functie: null } : null

  return (
    <div className="formulier compact">
      <label className="veld breed">
        <span className="veld-label">Titel</span>
        <input className="invoer" value={v.titel} maxLength={200} onChange={(e) => zet('titel', e.target.value)}
          aria-invalid={!!fout} aria-describedby={fout ? `${id}-fout` : undefined} />
        {fout && <span className="veld-fout" id={`${id}-fout`} role="alert">{fout}</span>}
      </label>
      <label className="veld breed">
        <span className="veld-label">Omschrijving</span>
        <textarea className="invoer" rows={3} value={v.omschrijving} onChange={(e) => zet('omschrijving', e.target.value)} />
      </label>
      <label className="veld">
        <span className="veld-label">Toegewezen aan</span>
        <select className="invoer" value={v.toegewezenAan && v.toegewezenAan !== ik?.id ? v.toegewezenAan : ''}
          onChange={(e) => {
            const m = mensen.find((x) => x.id === e.target.value) ?? (onbekend?.id === e.target.value ? onbekend : null)
            onV({ ...v, toegewezenAan: m?.id ?? null, toegewezenNaam: m?.naam ?? null })
          }}>
          <option value="">Mijzelf</option>
          {onbekend && <option value={onbekend.id}>{onbekend.naam}</option>}
          {anderen.map((m) => <option key={m.id} value={m.id}>{m.naam}{m.functie ? ` · ${m.functie}` : ''}</option>)}
        </select>
      </label>
      <label className="veld">
        <span className="veld-label">Deadline</span>
        <input type="date" className="invoer" value={v.deadline ?? ''} min={vandaagInvoer()}
          onChange={(e) => zet('deadline', e.target.value || null)} />
      </label>
      <div className="veld breed">
        <span className="veld-label" id={`${id}-prio`}>Prioriteit</span>
        <div className="segment" role="radiogroup" aria-labelledby={`${id}-prio`}>
          {PRIORITEITEN.map((p) => (
            <button key={p.key} type="button" role="radio" aria-checked={v.prioriteit === p.key}
              className={`segment-knop met-kleur t-${p.kleur} ${v.prioriteit === p.key ? 'aan' : ''}`}
              onClick={() => zet('prioriteit', p.key)}>
              <span className="punt" aria-hidden />{p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  De velden van een melding bij IT
 * ------------------------------------------------------------------ */

function TicketFormulier({ v, onV, fout }: { v: TicketVelden; onV: (v: TicketVelden) => void; fout?: string }) {
  const id = useId()
  const zet = <K extends keyof TicketVelden>(k: K, w: TicketVelden[K]) => onV({ ...v, [k]: w })
  return (
    <div className="formulier compact">
      <label className="veld breed">
        <span className="veld-label">Wat is er aan de hand</span>
        <input className="invoer" value={v.titel} maxLength={200} onChange={(e) => zet('titel', e.target.value)}
          aria-invalid={!!fout} aria-describedby={fout ? `${id}-fout` : undefined} />
        {fout && <span className="veld-fout" id={`${id}-fout`} role="alert">{fout}</span>}
      </label>
      <label className="veld breed">
        <span className="veld-label">Uitleg voor IT</span>
        <textarea className="invoer" rows={3} value={v.omschrijving} onChange={(e) => zet('omschrijving', e.target.value)} />
      </label>
      <label className="veld">
        <span className="veld-label">Waarover</span>
        <select className="invoer" value={v.categorie} onChange={(e) => zet('categorie', e.target.value as TicketVelden['categorie'])}>
          {IT_CATEGORIEEN.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </label>
      <div className="veld">
        <span className="veld-label" id={`${id}-prio`}>Hoe dringend</span>
        <div className="segment" role="radiogroup" aria-labelledby={`${id}-prio`}>
          {IT_PRIORITEIT.map((p) => (
            <button key={p.key} type="button" role="radio" aria-checked={v.prioriteit === p.key}
              className={`segment-knop met-kleur t-${p.kleur} ${v.prioriteit === p.key ? 'aan' : ''}`}
              onClick={() => zet('prioriteit', p.key)}>
              <span className="punt" aria-hidden />{p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
